import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import HTMLFlipBook from "react-pageflip";
import { Loader2, ChevronLeft, ChevronRight, Maximize2, Minimize2, Download, ExternalLink } from "lucide-react";

// Worker — same CDN already used by SurgicalCatalog
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * PdfFlipbook — reusable react-pageflip catalog viewer for any same-origin PDF.
 *
 * Drop-in usage:
 *   <PdfFlipbook fileUrl="/catalogs/2026-asc-sterilemate-catalog.pdf"
 *                title="2026 American Surgical Catalog"
 *                downloadName="2026-ASC-SterileMate-Catalog.pdf" />
 *
 * Resizes responsively, supports fullscreen toggle, and falls back to download
 * + open-in-new-tab buttons if the PDF fails to load.
 */
export default function PdfFlipbook({
  fileUrl,
  title = "Catalog",
  downloadName,
  initiallyOpen = true,
}) {
  const [numPages, setNumPages] = useState(0);
  const [flipPage, setFlipPage] = useState(0);
  const [open, setOpen] = useState(initiallyOpen);
  const [fullscreen, setFullscreen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const ref = useRef(null);
  const [dims, setDims] = useState({ width: 420, height: 560 });

  // Compute responsive page size — caps on both window width and height
  const compute = () => {
    if (typeof window === "undefined") return { width: 420, height: 560 };
    const padding = fullscreen ? 24 : 64;
    const maxW = Math.min(window.innerWidth - padding, 1280);
    const pageW = Math.floor(maxW / 2);
    const pageH = Math.floor(pageW * 1.35);
    const capH = Math.min(pageH, window.innerHeight - (fullscreen ? 120 : 220));
    const finalW = Math.floor(capH / 1.35);
    return { width: Math.min(pageW, finalW), height: Math.min(pageH, capH) };
  };

  useEffect(() => {
    setDims(compute());
    const onResize = () => setDims(compute());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullscreen]);

  // ESC closes fullscreen
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  const ToolbarButtons = (
    <div className="flex flex-wrap items-center gap-2">
      <button
        onClick={() => setOpen(!open)}
        className="px-3 h-9 rounded-md bg-cyan-500 hover:bg-cyan-400 text-slate-900 text-sm font-bold transition-colors"
        data-testid="flipbook-toggle-btn"
      >
        {open ? "Hide Flipbook" : "Open Flipbook"}
      </button>
      <button
        onClick={() => setFullscreen(true)}
        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-medium"
        data-testid="flipbook-fullscreen-btn"
      >
        <Maximize2 className="w-3.5 h-3.5" /> Fullscreen
      </button>
      <a
        href={fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-medium"
        data-testid="flipbook-open-tab"
      >
        <ExternalLink className="w-3.5 h-3.5" /> New Tab
      </a>
      <a
        href={fileUrl}
        download={downloadName}
        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-medium"
        data-testid="flipbook-download"
      >
        <Download className="w-3.5 h-3.5" /> Download
      </a>
    </div>
  );

  const Viewer = (
    <div className="flex flex-col items-center bg-slate-900/60 rounded-xl p-4 border border-slate-700" data-testid="flipbook-viewer">
      {loadError ? (
        <div className="py-12 text-center">
          <p className="text-rose-300 mb-3">Couldn&apos;t load the PDF in flip view.</p>
          <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold">
            <ExternalLink className="w-4 h-4" /> Open the PDF in a new tab
          </a>
        </div>
      ) : (
        <Document
          file={fileUrl}
          onLoadSuccess={({ numPages }) => { setNumPages(numPages); setLoadError(false); }}
          onLoadError={() => setLoadError(true)}
          loading={<div className="py-24 text-slate-400 flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading catalog…</div>}
        >
          {numPages > 0 && (
            <HTMLFlipBook
              width={dims.width}
              height={dims.height}
              size="fixed"
              minWidth={200}
              maxWidth={1280}
              minHeight={250}
              maxHeight={1700}
              showCover
              mobileScrollSupport
              onFlip={(e) => setFlipPage(e.data)}
              ref={ref}
              className="mx-auto"
            >
              {Array.from({ length: numPages }, (_, i) => (
                <div key={i} className="bg-white shadow-xl" style={{ width: dims.width, height: dims.height }}>
                  <Page
                    pageNumber={i + 1}
                    width={dims.width}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    loading={<div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
                  />
                </div>
              ))}
            </HTMLFlipBook>
          )}
        </Document>
      )}

      {numPages > 0 && !loadError && (
        <div className="flex items-center gap-4 mt-4 bg-slate-800/70 rounded-lg px-4 py-2">
          <button onClick={() => ref.current?.pageFlip()?.flipPrev()} className="p-1.5 hover:bg-slate-700 rounded" data-testid="flipbook-prev"><ChevronLeft className="w-4 h-4 text-white" /></button>
          <span className="text-xs text-slate-300 font-medium" data-testid="flipbook-page-indicator">
            Page {Math.min(flipPage + 1, numPages)} / {numPages}
          </span>
          <button onClick={() => ref.current?.pageFlip()?.flipNext()} className="p-1.5 hover:bg-slate-700 rounded" data-testid="flipbook-next"><ChevronRight className="w-4 h-4 text-white" /></button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="bg-gradient-to-r from-[#0b2447] via-[#0a2a4d] to-[#0b2447] border border-cyan-500/20 rounded-2xl p-6" data-testid="flipbook-section">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-cyan-500/20 border border-cyan-400/30 flex items-center justify-center text-cyan-200 font-bold text-lg">📘</div>
            <div>
              <h3 className="text-lg font-bold text-white">{title}</h3>
              <p className="text-xs text-slate-400">{numPages ? `${numPages} pages · Flip through like a real brochure` : "Loading…"}</p>
            </div>
          </div>
          {ToolbarButtons}
        </div>
        {open && <div className="mt-4">{Viewer}</div>}
      </div>

      {/* Fullscreen overlay */}
      {fullscreen && (
        <div className="fixed inset-0 z-[80] bg-slate-900/95 backdrop-blur flex flex-col" data-testid="flipbook-fullscreen-overlay">
          <div className="flex items-center justify-between px-6 h-14 border-b border-slate-700/60">
            <h4 className="text-white font-bold text-sm">{title} — Fullscreen</h4>
            <div className="flex gap-2">
              <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-medium">
                <ExternalLink className="w-3.5 h-3.5" /> New Tab
              </a>
              <button
                onClick={() => setFullscreen(false)}
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-medium"
                data-testid="flipbook-fullscreen-close"
              >
                <Minimize2 className="w-3.5 h-3.5" /> Exit (Esc)
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-4 flex items-start justify-center">
            {Viewer}
          </div>
        </div>
      )}
    </>
  );
}
